<template>
  <view v-if="visible" class="sidebar-overlay" @click="handleClose" @touchmove.stop.prevent>
    <view
      class="sidebar-container"
      :class="{ 'sidebar-show': showContent }"
      @click.stop
      @touchmove.stop
    >
      <scroll-view class="menu-scroll" scroll-y :show-scrollbar="false">
        <!-- 已登录：用户信息 -->
        <view v-if="walletStore.isAuthenticated" class="user-section" @click="goToWallet">
          <image
            class="avatar"
            :src="DEFAULT_USER_AVATAR"
            mode="aspectFill"
          />
          <view class="user-info">
            <text class="user-name">{{ displayName }}</text>
            <view class="address-row">
              <text class="user-address">ID: {{ walletStore.shortAddress }}</text>
              <image
                class="copy-icon"
                src="/static/image/lanhu/sidebar/copy.webp"
                mode="aspectFit"
                @click.stop="copyToClipboard(walletStore.address)"
              />
            </view>
          </view>
        </view>

        <!-- 未登录 -->
        <view v-else class="login-section" @click="handleLoginClick">
          <image
            class="login-avatar"
            :src="DEFAULT_USER_AVATAR"
            mode="aspectFill"
          />
          <text class="login-text">{{ t('sidebar.loginAccount') }}</text>
          <image
            class="menu-arrow"
            src="/static/image/lanhu/sidebar/chevron.webp"
            mode="aspectFit"
          />
        </view>

        <view class="divider" />

        <!-- 菜单：Lanhu 顺序 -->
        <view class="menu-list">
          <view
            v-for="item in menuItems"
            :key="item.key"
            class="menu-item"
            @click="handleFeatureClick(item.key)"
          >
            <view class="menu-icon-wrap" :class="{ 'is-circle': item.circle }">
              <image :src="item.icon" class="menu-icon" :class="{ 'icon-inset': item.circle }" mode="aspectFit" />
            </view>
            <text class="menu-text">{{ item.name }}</text>
            <image
              class="menu-arrow"
              src="/static/image/lanhu/sidebar/chevron.webp"
              mode="aspectFit"
            />
          </view>
        </view>

        <view class="divider divider-bottom" />

        <view
          v-if="walletStore.isAuthenticated"
          class="logout-btn"
          @click="handleLogout"
        >
          <text class="logout-text">{{ t('sidebar.logoutWallet') }}</text>
        </view>
      </scroll-view>

      <confirm-modal
        v-model:show="confirmModal.show"
        :title="confirmModal.title"
        :cancel-text="confirmModal.cancelText"
        :confirm-text="confirmModal.confirmText"
        @confirm="confirmModal.onConfirm"
        @cancel="confirmModal.onCancel"
      />
    </view>
  </view>

  <richtext-modal
    :visible="showInfoModal"
    :title="infoModalTitle"
    :html="infoModalContent"
    @close="closeInfoModal"
  />
</template>

<script setup lang="ts">
import ConfirmModal from '@/components/confirm-modal/index.vue';
import RichtextModal from '@/components/richtext-modal/index.vue';
import { computed, reactive, ref, toRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useWalletStore } from '@/store/wallet';
import { useProfileStore } from '@/store/profile';
import { useConfigStore } from '@/store/config';
import { useAnnouncementsStore } from '@/store/announcements';
import { DEFAULT_USER_AVATAR } from '@/common/constants';
import { triggerLogin } from '@/common/auth';
import { useScrollLock } from '@/composables/useScrollLock';
import { useHideTabbarOnOverlay } from '@/composables/useHideTabbarOnOverlay';
import { resolveBrandedLegalText } from '@/common/utils/localizeText';

const ICON = {
  copyTrade: '/static/image/lanhu/sidebar/copy-trade.webp',
  invite: '/static/image/lanhu/sidebar/invite.webp',
  team: '/static/image/lanhu/sidebar/team.webp',
  customer: '/static/image/lanhu/sidebar/customer.webp',
  help: '/static/image/lanhu/sidebar/help.webp',
  community: '/static/image/lanhu/sidebar/community.webp',
  terms: '/static/image/lanhu/sidebar/terms.webp',
  privacy: '/static/image/lanhu/sidebar/privacy.webp',
  about: '/static/image/lanhu/sidebar/about.webp',
};

const props = defineProps({ visible: { type: Boolean, default: false } });
const menuVisible = toRef(props, 'visible');
useScrollLock(menuVisible);
useHideTabbarOnOverlay(menuVisible);
const emit = defineEmits(['close']);

const { t } = useI18n();
const walletStore = useWalletStore();
const profile = useProfileStore();
const configStore = useConfigStore();
const annStore = useAnnouncementsStore();
const showContent = ref(false);
const showInfoModal = ref(false);
const infoModalTitle = ref('');
const infoModalContent = ref('');

const confirmModal = reactive({
  show: false,
  title: '',
  cancelText: '',
  confirmText: '',
  onConfirm: () => {},
  onCancel: () => {},
});

watch(() => props.visible, (val) => {
  if (val) {
    setTimeout(() => { showContent.value = true; }, 10);
    // 打开侧栏时对齐 MetaMask/DApp 当前账户，避免扩展已切号但 ID 仍显示旧会话
    void walletStore.syncProviderAccount('sidebar').finally(() => {
      void annStore.fetchUnreadCount();
      void profile.refresh();
    });
  } else {
    showContent.value = false;
  }
});

/** Account nickname with the existing brand fallback. */
const displayName = computed(() => profile.nickname || t('sidebar.defaultName'));

const menuItems = computed(() => [
  { name: t('copyTrade.menuTitle'), icon: ICON.copyTrade, key: 'copyTrade' },
  { name: t('my.features.invite'), icon: ICON.invite, key: 'invite' },
  { name: t('my.features.team'), icon: ICON.team, key: 'team' },
  { name: t('my.features.communitySquare'), icon: ICON.help, key: 'communitySquare' },
  { name: t('my.features.createEvent'), icon: ICON.customer, key: 'createEvent', circle: true },
  { name: t('my.features.community'), icon: ICON.community, key: 'community' },
  { name: t('my.features.terms'), icon: ICON.terms, key: 'terms' },
  { name: t('my.features.privacy'), icon: ICON.privacy, key: 'privacy' },
  { name: t('my.features.aboutUs'), icon: ICON.about, key: 'about' },
]);

const handleClose = () => {
  showContent.value = false;
  setTimeout(() => { emit('close'); }, 300);
};

const goToWallet = () => {
  handleClose();
  uni.navigateTo({ url: '/pages/wallet/index' });
};

const handleLoginClick = () => {
  handleClose();
  setTimeout(() => {
    triggerLogin();
  }, 350);
};

const openInfoModal = (title: string, content: string) => {
  infoModalTitle.value = title;
  infoModalContent.value = String(content || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br/>');
  showInfoModal.value = true;
};

const closeInfoModal = () => {
  showInfoModal.value = false;
};

const handleFeatureClick = (key: string) => {
  switch (key) {
    case 'copyTrade':
      handleClose();
      uni.navigateTo({ url: '/pages/copy-trade/index' });
      break;
    case 'invite':
      handleClose();
      uni.navigateTo({ url: '/pages/invite/index' });
      break;
    case 'team':
      handleClose();
      uni.navigateTo({ url: '/pages/team/index' });
      break;
    case 'about':
      handleClose();
      uni.navigateTo({ url: '/pages/about/index' });
      break;
    case 'communitySquare':
    case 'createEvent':
      uni.showToast({ title: t('common.developing'), icon: 'none' });
      break;
    case 'community':
      handleClose();
      uni.navigateTo({ url: '/pages/community/index' });
      break;
    case 'terms': {
      const title = t('my.features.terms');
      const content = resolveBrandedLegalText(
        configStore.getConfigValue('TERM_OF_SERVICE', 'mockData.legal.terms'),
        'mockData.legal.terms',
        t
      );
      handleClose();
      setTimeout(() => openInfoModal(title, content), 320);
      break;
    }
    case 'privacy': {
      const title = t('my.features.privacy');
      const content = resolveBrandedLegalText(
        configStore.getConfigValue('PRIVACY_POLICY', 'mockData.legal.privacy'),
        'mockData.legal.privacy',
        t
      );
      handleClose();
      setTimeout(() => openInfoModal(title, content), 320);
      break;
    }
    default:
      break;
  }
};

const handleLogout = () => {
  confirmModal.title = t('walletProfile.confirmLogout');
  confirmModal.cancelText = t('common.cancel');
  confirmModal.confirmText = t('common.confirm');
  confirmModal.onConfirm = async () => {
    confirmModal.show = false;
    // 立刻收起侧边栏并给反馈，再跑断连（WC 可能慢）
    handleClose();
    uni.showToast({
      title: t('walletProfile.loggingOut'),
      icon: 'loading',
      duration: 1500,
      mask: true,
    });
    try {
      await walletStore.disconnect();
    } catch (error) {
      console.error('[Sidebar] logout failed:', error);
    }
  };
  confirmModal.onCancel = () => {
    confirmModal.show = false;
  };
  confirmModal.show = true;
};

const copyToClipboard = (text: string) => {
  if (!text) return;
  uni.setClipboardData({
    data: text,
    success: () => { uni.showToast({ title: t('invite.toasts.copySuccess'), icon: 'success' }); },
  });
};
</script>

<style lang="scss" scoped>
.sidebar-overlay {
  position: fixed;
  top: 0;
  bottom: 0;
  left: max(0px, calc((100vw - min(100vw, var(--app-max-width, 750px))) / 2));
  width: min(100vw, var(--app-max-width, 750px));
  /* 高于 custom-tabbar(10030)，抽屉盖住底栏而非藏底栏 */
  z-index: 10060;
  background-color: rgba(0, 0, 0, 0.6);
}

.sidebar-container {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 638rpx;
  max-width: 85%;
  background-color: #f7f7f7;
  transform: translateX(-100%);
  transition: transform 0.3s ease;
  display: flex;
  flex-direction: column;
  padding-top: var(--status-bar-height);
  box-sizing: border-box;

  &.sidebar-show {
    transform: translateX(0);
  }
}

.menu-scroll {
  flex: 1;
  height: 100%;
}

.user-section {
  display: flex;
  align-items: center;
  padding: 26rpx 0 0 34rpx;
  gap: 26rpx;

  &:active {
    opacity: 0.85;
  }

  .avatar {
    width: 124rpx;
    height: 124rpx;
    border-radius: 50%;
    overflow: hidden;
    flex-shrink: 0;
    background: #3aa0ff;
  }

  .user-info {
    flex: 1;
    min-width: 0;
    padding-top: 22rpx;

    .user-name {
      font-family: 'Noto Sans SC', sans-serif;
      font-size: 38rpx;
      font-weight: 700;
      color: #171b22;
      line-height: 38rpx;
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .address-row {
      display: flex;
      align-items: center;
      gap: 10rpx;
      margin-top: 22rpx;

      .user-address {
        font-family: 'Noto Sans SC', sans-serif;
        font-size: 24rpx;
        font-weight: 400;
        color: #6b6e73;
        line-height: 24rpx;
      }

      .copy-icon {
        width: 18rpx;
        height: 18rpx;
        flex-shrink: 0;
      }
    }
  }
}

.login-section {
  display: flex;
  align-items: center;
  padding: 40rpx 34rpx 10rpx;
  gap: 24rpx;

  &:active {
    opacity: 0.85;
  }

  .login-avatar {
    width: 100rpx;
    height: 100rpx;
    border-radius: 50%;
    overflow: hidden;
    flex-shrink: 0;
    background: #3aa0ff;
  }

  .login-text {
    flex: 1;
    font-family: 'Noto Sans SC', sans-serif;
    font-size: 32rpx;
    font-weight: 500;
    color: #171b22;
  }
}

.divider {
  width: 572rpx;
  height: 1rpx;
  background: #ecedea;
  margin: 30rpx 0 0 34rpx;

  &.divider-bottom {
    margin-top: 53rpx;
  }
}

.menu-list {
  padding: 0 30rpx 0 30rpx;
}

.menu-item {
  display: flex;
  align-items: center;
  height: 60rpx;
  margin-top: 50rpx;

  &:first-child {
    margin-top: 30rpx;
  }

  &:active {
    opacity: 0.7;
  }
}

.menu-icon-wrap {
  width: 60rpx;
  height: 60rpx;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  &.is-circle {
    .menu-icon {
      width: 41rpx;
      height: 41rpx;
      border-radius: 50%;
      background: #5ec300;
      padding: 6rpx;
      box-sizing: border-box;
    }
  }
}

.menu-icon {
  width: 60rpx;
  height: 60rpx;
}

.menu-text {
  flex: 1;
  margin-left: 24rpx;
  font-family: 'MiSans', 'Noto Sans SC', sans-serif;
  font-size: 28rpx;
  font-weight: 400;
  color: #000000;
  line-height: 28rpx;
}

.menu-arrow {
  width: 10rpx;
  height: 21rpx;
  flex-shrink: 0;
  margin-right: 4rpx;
}

.logout-btn {
  width: 100%;
  padding: 47rpx 0 80rpx;
  display: flex;
  align-items: center;
  justify-content: center;

  &:active {
    opacity: 0.75;
  }

  .logout-text {
    font-family: 'Noto Sans SC', sans-serif;
    font-size: 30rpx;
    font-weight: 500;
    color: #5ec300;
    line-height: 40rpx;
    text-align: center;
  }
}
</style>
